<script setup>
import { ref, computed, watch, nextTick } from "vue";
import draggable from "vuedraggable";

// Props
const props = defineProps({
  modelValue: {
    type: Array,
    required: true,
  },
  items: {
    type: Array,
    required: true,
  },
  label: String,
  searchModel: String,
});

const emit = defineEmits([
  "update:modelValue",
  "update:searchModel",
  "search-input",
]);

// Local state
const menu = ref(false);
const search = ref(props.searchModel ?? "");
const activatorRef = ref(null);
const searchInputRef = ref(null);

const selected = computed({
  get: () => props.modelValue,
  set: (val) => emit("update:modelValue", val),
});

// Filtering visible list (optional fallback, in case props.items is static)
const filteredItems = computed(() =>
  props.items.filter(
    (item) =>
      item.name.toLowerCase().includes(search.value.toLowerCase()) &&
      !selected.value.some((s) => s.id === item.id)
  )
);

// Debounce utility
function debounce(fn, delay) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
}

// Search behavior
const debouncedSearch = debounce((val) => {
  emit("search-input", val);
}, 300);

watch(search, (val) => {
  emit("update:searchModel", val);
  debouncedSearch(val);
});

watch(
  () => props.searchModel,
  (val) => {
    if (search.value !== val) {
      search.value = val;
    }
  }
);

// Open menu when results arrive
watch(
  () => props.items,
  (newItems) => {
    if (search.value && newItems.length > 0 && !menu.value) {
      menu.value = true;
    }
  }
);

// Actions
const select = (item) => {
  selected.value = [...selected.value, item];
  search.value = "";
  nextTick(() => searchInputRef.value?.focus());
};

const remove = (index) => {
  const copy = [...selected.value];
  copy.splice(index, 1);
  selected.value = copy;
};

const focusSearch = () => {
  nextTick(() => {
    searchInputRef.value?.focus();
    menu.value = true;
  });
};
</script>

<template>
  <div>
    <v-label class="mb-1">
      {{ label }}
    </v-label>

    <v-sheet
      ref="activatorRef"
      class="d-flex flex-wrap align-center pa-2 border border-opacity-25"
      elevation="0"
      color="transparent"
      rounded
      style="min-height: 56px; cursor: text"
      @click="focusSearch"
    >
      <draggable
        v-model="selected"
        item-key="id"
        tag="div"
        class="d-flex flex-wrap"
        :animation="150"
      >
        <template #item="{ element, index }">
          <v-chip
            class="ma-1 draggable-chip"
            size="small"
            closable
            @click:close="remove(index)"
          >
            {{ element.name }}
          </v-chip>
        </template>
      </draggable>

      <v-text-field
        ref="searchInputRef"
        v-model="search"
        variant="plain"
        density="compact"
        hide-details
        single-line
        class="ml-2 flex-grow-1 custom-text-input"
        :placeholder="
          selected != null && selected.length <= 0
            ? 'Start Typing to Search'
            : ''
        "
        @focus="menu = true"
      />
    </v-sheet>

    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      :activator="activatorRef"
      transition="slide-y-transition"
      location="bottom"
      content-class="custom-dropdown-menu--scrollable"
    >
      <v-list>
        <v-list-item
          v-for="item in filteredItems"
          :key="item.id"
          @click="select(item)"
        >
          <v-list-item-title>{{ item.name }}</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="filteredItems.length === 0">
          <v-list-item-title>No matches</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<style lang="scss">
.custom-text-input {
  input {
    padding: 0;
  }
}
.custom-dropdown-menu--scrollable {
  max-height: 300px !important;
  overflow-y: auto;
}
.draggable-chip {
  cursor: pointer;
}
</style>
